// A poll that only runs while the tab is actually being looked at.
//
// The client-approval screens were built against an in-process mock, where a
// 4-second interval cost nothing and made the other side's activity appear
// immediately. Against the real API that is a request every 4 seconds per open
// tab per staff member, forever, including tabs nobody has looked at since
// Tuesday (ourteam-frontend#3676).
//
// So: fetch on mount, fetch again whenever the tab comes back to the front, and
// otherwise tick slowly while visible. Coming back to the tab is the moment a
// reader actually wants fresh data, which is why the focus fetch matters more
// than the interval — the interval is only there so a tab left open on a second
// monitor still moves.
//
// When the backend offers a push channel, replace the interval here and both
// callers get it.

import { useEffect, useRef } from "react";

export function useVisiblePoll(fetcher: () => void, everyMs = 60_000, deps: unknown[] = []) {
    // Kept in a ref so a caller can pass an inline closure without restarting
    // the timer on every render.
    const latest = useRef(fetcher);
    latest.current = fetcher;

    useEffect(() => {
        const run = () => latest.current();
        run();

        let timer: number | undefined;
        const start = () => {
            if (timer === undefined) timer = window.setInterval(run, everyMs);
        };
        const stop = () => {
            if (timer !== undefined) { window.clearInterval(timer); timer = undefined; }
        };

        const onVisibility = () => {
            if (document.visibilityState === "visible") { run(); start(); } else stop();
        };

        if (document.visibilityState === "visible") start();
        document.addEventListener("visibilitychange", onVisibility);
        return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [everyMs, ...deps]);
}
